/**
 * Morse — Merkwörter zum SPRECHEN: statt „M“ darf man „Monitor“ sagen. (16.10.2026)
 *
 * Martin (19:58): „Mir gefallen die Buchstaben wie Motor, Uniform. Ich möchte das erweitern, dass ich entweder nur den
 * Buchstaben sage oder aber auch den Begriff, zum Beispiel ‚Monitor‘, ‚Uniform‘ — damit ich so üben kann.“
 *
 * Quellen, EINE Tabelle am Ende (`SPRECH_MERKWOERTER`, Wort klein → Buchstabe):
 *   1. die geprüften Eselsbrücken aus `morse-merkwoerter.ts` (Mo-tor → „motor“) — nur einwortige, die mit ihrem Buchstaben
 *      BEGINNEN („Kon-di-ti-on“ für X und „Co-lo-ra-do“ für Q fallen so heraus);
 *   2. die Zusatzliste hier (Monitor, Michael …) — von Hand ausgesucht, je Buchstabe ein paar geläufige Wörter.
 * Die Buchstabiernamen (Anton, Mike, Uniform …) stehen in morse-sprechen-leser.ts und gelten weiter.
 *
 * Regeln (tests/shared/morse-sprech-merkwoerter.test.ts): jedes Wort beginnt mit seinem Buchstaben (ae/oe/ue für Ä/Ö/Ü),
 * kein Wort bedeutet zwei Buchstaben (auch nicht gegen die Buchstabiernamen/Buchstabennamen), nur Kleinbuchstaben ohne
 * Leerzeichen. Ein Wort gilt nur ALS GANZES gesprochenes Wort — „Maus“ (nicht in der Liste) ist kein M.
 * ⚠️ Nichts aufnehmen, was ein Füllwort oder ein Buchstabenname ist („ja“, „so“, „auf“, „eh“) — das wäre in jedem Satz ein Buchstabe.
 */
import { MORSE_MERKWOERTER } from "./morse-merkwoerter.js";

/** Zusatzwörter je Buchstabe (klein; Umlaute auch in der Schreibweise ae/oe/ue, wie eine Erkennung sie liefert). */
const ZUSATZ: Readonly<Record<string, readonly string[]>> = {
  A: ["auto", "apfel", "affe", "antenne", "anna"],
  B: ["berlin", "baum", "ball", "boot", "bernd"],
  C: ["cola", "computer", "clown"],
  D: ["dach", "dose", "daniel", "dorf"],
  E: ["elefant", "esel", "erde", "emma"],
  F: ["fisch", "feuer", "fabrik", "film"],
  G: ["garten", "gabel", "gans"],
  H: ["hund", "hammer", "himmel"],
  I: ["insel", "indien", "inge"],
  J: ["jaguar", "junge", "januar"],
  K: ["kamel", "katze", "kaffee", "kappa"],
  L: ["löwe", "loewe", "lampe", "leo", "lilie"],
  M: ["monitor", "motor", "michael"],
  N: ["nase", "nudel", "nikolaus", "norbert"],
  O: ["ofen", "orange", "olaf", "oma"],
  P: ["papier", "pizza", "peter", "paris"],
  Q: ["quark", "quadrat", "quitte"],
  R: ["rose", "radio", "rakete", "robert"],
  S: ["sonne", "sofa", "stefan", "sandra"],
  T: ["tisch", "tiger", "thomas", "tomate"],
  U: ["uniform", "unter", "uhr", "ufo", "uwe"],
  V: ["vogel", "vase", "vater"],
  W: ["wasser", "wolke", "wolf", "wiese"],
  X: ["xylophon"],
  Y: ["yoga", "yacht", "yeti"],
  Z: ["zebra", "zitrone", "zucker", "zug", "zeta"],
  Ä: ["äpfel", "aepfel", "ägypten", "aegypten", "ärmel", "aermel"],
  Ö: ["öl", "oel", "österreich", "oesterreich"],
  Ü: ["übung", "uebung", "überfall", "ueberfall"],
};

/** Die einwortigen Eselsbrücken (Silben ohne Bindestrich), die mit ihrem Buchstaben beginnen. */
function ausEselsbruecken(): [string, string][] {
  const aus: [string, string][] = [];
  for (const [z, d] of Object.entries(MORSE_MERKWOERTER)) {
    if (/\s/.test(d.merkwort)) continue;
    const wort = d.merkwort.replace(/-/g, "").toLowerCase();
    if (wort.startsWith(z.toLowerCase())) aus.push([wort, z]);
  }
  return aus;
}

function baue(): Readonly<Record<string, string>> {
  const t: Record<string, string> = Object.create(null) as Record<string, string>;
  const setze = (wort: string, z: string): void => {
    t[wort.normalize("NFC")] = z;
  };
  for (const [wort, z] of ausEselsbruecken()) setze(wort, z);
  for (const [z, liste] of Object.entries(ZUSATZ)) for (const wort of liste) setze(wort, z);
  return t;
}

/** Merkwort (klein, ganzes Wort) → Buchstabe. */
export const SPRECH_MERKWOERTER: Readonly<Record<string, string>> = baue();

/** Der Buchstabe zu einem ganzen, kleingeschriebenen Wort — oder `undefined`. */
export function merkwortBuchstabe(wort: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(SPRECH_MERKWOERTER, wort) ? SPRECH_MERKWOERTER[wort] : undefined;
}
